import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import I18nContext from './i18n';
import translationFiles from '../translation_files';
import { getApiClient } from '../api_client/get';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';
import TranslationScope from '../translations/translation_scope';

const KEY = 'translation_files';
function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

// Get all translation files from the store
function getAllTranslationFilesFromStore() {
  try {
    const infoJson = _getStore().getItem(KEY);
    if (infoJson == null) return {};
    return JSON.parse(infoJson);
  } catch (error) {
    console.log(error);
    return {};
  }
}

// Store all translation files initially in persistent storage
function storeAllTranslationFilesInStore() {
  const allTranslationFiles = Object.keys(translationFiles).reduce((acc, locale) => {
    acc[locale] = {
      hash: translationFiles[locale]?.hash,
      content: translationFiles[locale]?.content
    };
    return acc;
  }, {});

  _getStore().setItem(KEY, JSON.stringify(allTranslationFiles));
}

// Update a specific locale's translation file in the store
function updateTranslationFileForLocaleInStore(locale, translationFile) {
  return new Promise((resolve, reject) => {
    const mutex = _getMutex();
    const store = _getStore();
    mutex.lock(KEY)
      .then(() => {
        const allTranslationFiles = getAllTranslationFilesFromStore();
        allTranslationFiles[locale] = {
          hash: translationFile.hash,
          content: translationFile.content
        };
        store.setItem(KEY, JSON.stringify(allTranslationFiles));
        mutex.release(KEY)
          .then(resolve)
          .catch(reject);
      })
      .catch(reject);
  });
}

const TokenTranslationContext = createContext();

export const TokenTranslationProvider = ({ children }) => {
  const i18nSettings = useContext(I18nContext);
  const locale = i18nSettings.getLocale();
  const defaultHash = translationFiles[locale]?.hash;

  const [translations, setTranslations] = useState(() => {
    const fromStore = getAllTranslationFilesFromStore();
    return Object.keys(translationFiles).reduce((acc, locale) => {
      acc[locale] = fromStore[locale]?.content || translationFiles[locale]?.content || {};
      return acc;
    }, {});
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      try {
        const allTranslationFiles = getAllTranslationFilesFromStore();
        if (Object.keys(allTranslationFiles).length === 0) {
          storeAllTranslationFilesInStore();
        }

        const currentHash = allTranslationFiles[locale]?.hash || defaultHash;

        const response = await getApiClient().getTranslationFile(TranslationScope.FOR_WEB, locale, currentHash);

        if (response.status === 200) {
          const {
            file_hash: newHash,
            file_contents: newTranslations,
            locale: updatedLocale
          } = response.data.translation_file;

          await updateTranslationFileForLocaleInStore(updatedLocale, {
            hash: newHash,
            content: newTranslations
          });

          setTranslations((prev) => ({
            ...prev,
            [locale]: newTranslations
          }));
        }
        // If 204, just keep the current defaultTranslations already in state
      } catch (err) {
        console.error('Failed to initialize translations:', err);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [locale, defaultHash]);

  const translate = useCallback(
    (token) => {
      const translated = translations[locale]?.[token];
      return translated ?? '';
    },
    [locale, translations]
  );

  const value = useMemo(() => ({
    translations,
    translate,
    locale,
    updateTranslation: (locale, translationFile) =>
      updateTranslationFileForLocaleInStore(locale, translationFile)
  }), [translations, translate, locale]);

  if (loading || !translations[locale]) return null;

  return (
    <TokenTranslationContext.Provider value={value}>
      {children}
    </TokenTranslationContext.Provider>
  );
};

export const useTokenTranslate = () => {
  const context = useContext(TokenTranslationContext);
  if (!context) throw new Error('TokenTranslationContext must be used within a TokenTranslationProvider');
  return context;
};

export default TokenTranslationContext;
